import { z } from "zod";
import type { QueryParams } from "../httpClient";
import { CompactUserSchema, PublicExpertLevelSchema } from "./expert-levels";

export const WalletTransactionKindSchema = z.union([
    z.literal("expert_quote_settlement"),
    z.literal("admin_credit"),
    z.literal("admin_debit"),
    z.literal("admin_hold"),
    z.literal("admin_hold_debit"),
    z.literal("admin_external_payment"),
    z.literal("admin_gross_sales_credit"),
    z.literal("admin_gross_sales_debit"),
    z.literal("admin_net_credit"),
    z.literal("admin_net_debit"),
    z.literal("hold_release"),
    z.literal("refund"),
    z.literal("refund_from_hold"),
]);

export const AdminWalletTransactionKindSchema = z.union([
    z.literal("admin_credit"),
    z.literal("admin_debit"),
    z.literal("admin_hold"),
    z.literal("admin_hold_debit"),
    z.literal("admin_external_payment"),
    z.literal("admin_gross_sales_credit"),
    z.literal("admin_gross_sales_debit"),
    z.literal("admin_net_credit"),
    z.literal("admin_net_debit"),
]);

export const WalletTransactionDirectionSchema = z.union([
    z.literal("credit"),
    z.literal("debit"),
    z.literal("transfer"),
]);

export const AdminWalletHoldSourceSchema = z.union([
    z.literal("available"),
    z.literal("direct"),
]);

export const OrganizationWalletSchema = z.object({
    id: z.number().int(),
    organization_id: z.number().int(),
    currency: z.string(),
    available_balance_cents: z.number().int(),
    hold_balance_cents: z.number().int(),
    lifetime_gross_cents: z.number().int(),
    lifetime_net_cents: z.number().int(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
}).passthrough();

export const ExpertWalletSummarySchema = z.object({
    wallet: OrganizationWalletSchema,
    total_gross_sales_cents: z.number().int(),
    total_net_cents: z.number().int(),
    available_balance_cents: z.number().int(),
    hold_balance_cents: z.number().int(),
    currency: z.string(),
    allow_payouts: z.boolean().optional(),
}).passthrough();

export const PayoutRequestStatusSchema = z.union([
    z.literal("pending"),
    z.literal("done"),
    z.literal("rejected"),
]);

export const AdminWalletAvailableAllocationSchema = z.object({
    source_transaction_id: z.number().int(),
    amount_cents: z.number().int(),
    source_transaction_kind: WalletTransactionKindSchema.nullable(),
    source_available_delta_cents: z.number().int().nullable().optional(),
    source_remaining_available_cents: z.number().int().nullable().optional(),
    quote_id: z.number().int().nullable(),
    project_id: z.number().int().nullable(),
    hold_transaction_id: z.number().int().nullable(),
}).passthrough();

export const ExpertPayoutRequestSchema = z.object({
    id: z.number().int(),
    amount_cents: z.number().int(),
    currency: z.string(),
    status: PayoutRequestStatusSchema,
    notes: z.string().nullable(),
    public_notes: z.string().nullable(),
    wallet_transaction_id: z.number().int().nullable(),
    decided_at: z.string().nullable(),
    created_at: z.string().nullable(),
}).passthrough();

export const AdminPayoutRequestSchema = z.object({
    id: z.number().int(),
    organization: z.object({
        id: z.number().int(),
        name: z.string(),
        expert_id: z.number().int().nullable().optional(),
    }).passthrough().nullable().optional(),
    requested_by: z.object({
        id: z.number().int(),
        name: z.string(),
        email: z.string(),
    }).passthrough().nullable().optional(),
    amount_cents: z.number().int(),
    currency: z.string(),
    status: PayoutRequestStatusSchema,
    notes: z.string().nullable(),
    public_notes: z.string().nullable(),
    internal_notes: z.string().nullable(),
    wallet_transaction_id: z.number().int().nullable(),
    decided_by: z.object({
        id: z.number().int(),
        name: z.string(),
    }).passthrough().nullable().optional(),
    decided_at: z.string().nullable(),
    created_at: z.string().nullable(),
    available_allocations: z.array(AdminWalletAvailableAllocationSchema).nullable().optional(),
    unallocated_available_cents: z.number().int().nullable().optional(),
}).passthrough();

export const AdminPayoutApprovalPreviewSchema = z.object({
    payout_request_id: z.number().int(),
    amount_cents: z.number().int(),
    currency: z.string(),
    available_allocations: z.array(AdminWalletAvailableAllocationSchema),
    unallocated_available_cents: z.number().int(),
}).passthrough();

export const ExpertCreatePayoutRequestRequestSchema = z.object({
    acting_org_id: z.number().int().min(1),
    amount_cents: z.number().int().min(1),
    notes: z.string().max(2000).nullable().optional(),
});

export const AdminDecidePayoutRequestRequestSchema = z.object({
    public_notes: z.string().max(2000).nullable().optional(),
    internal_notes: z.string().max(2000).nullable().optional(),
});

export const ExpertWalletLevelProgressSchema = z.object({
    current_level: PublicExpertLevelSchema.nullable(),
    next_level: PublicExpertLevelSchema.nullable(),
    lifetime_gross_cents: z.number().int(),
    remaining_gross_cents: z.number().int().nullable(),
    currency: z.string(),
}).passthrough();

export const ExpertQuoteFeeEstimateLevelSchema = z.object({
    id: z.number().int(),
    public_title: z.string(),
    public_description: z.string().nullable().optional(),
    commission_rate_bps: z.number().int(),
    auto_enroll_threshold_gross_cents: z.number().int().optional(),
}).passthrough();

export const ExpertQuoteFeeEstimateSchema = z.object({
    transaction_kind: z.literal("expert_quote_settlement_estimate"),
    amount_cents: z.number().int(),
    currency: z.string(),
    gross_amount_cents: z.number().int(),
    provider_fee_cents: z.number().int(),
    provider_fee_details: z.unknown().nullable(),
    commission_cents: z.number().int(),
    net_amount_cents: z.number().int(),
    expert_level_id: z.number().int().nullable(),
    expert_level: ExpertQuoteFeeEstimateLevelSchema.nullable(),
    estimate: z.boolean(),
}).passthrough();

export const ExpertWalletTransactionSchema = z.object({
    id: z.number().int(),
    transaction_kind: WalletTransactionKindSchema,
    direction: WalletTransactionDirectionSchema,
    amount_cents: z.number().int(),
    currency: z.string(),
    available_delta_cents: z.number().int(),
    hold_delta_cents: z.number().int(),
    remaining_hold_cents: z.number().int().nullable(),
    released_hold_cents: z.number().int().optional(),
    debited_hold_cents: z.number().int().optional(),
    refunded_hold_cents: z.number().int().optional(),
    public_notes: z.string().nullable(),
    created_at: z.string().nullable(),
    related_transaction_id: z.number().int().nullable(),
    release_transaction_ids: z.array(z.number().int()).optional(),
    quote_id: z.number().int().nullable(),
    project_id: z.number().int().nullable(),
    gross_amount_cents: z.number().int().nullable(),
    provider_fee_cents: z.number().int().nullable(),
    provider_fee_details: z.unknown().nullable(),
    commission_cents: z.number().int().nullable(),
    net_amount_cents: z.number().int().nullable(),
    expert_level_id: z.number().int().nullable(),
    expert_level: z.unknown().nullable(),
}).passthrough();

export const AdminWalletResponseSchema = z.object({
    wallet: OrganizationWalletSchema,
}).passthrough();

export const AdminWalletFundedAllocationSchema = z.object({
    debit_transaction_id: z.number().int(),
    amount_cents: z.number().int(),
    debit_transaction_kind: WalletTransactionKindSchema.nullable(),
    payout_request_id: z.number().int().nullable(),
}).passthrough();

export const AdminWalletTransactionSchema = z.object({
    id: z.number().int(),
    wallet_id: z.number().int(),
    organization_id: z.number().int(),
    transaction_kind: WalletTransactionKindSchema,
    direction: WalletTransactionDirectionSchema,
    amount_cents: z.number().int(),
    available_delta_cents: z.number().int(),
    hold_delta_cents: z.number().int(),
    currency: z.string(),
    idempotency_key: z.string(),
    related_transaction_id: z.number().int().nullable(),
    gross_amount_cents: z.number().int().nullable(),
    provider_fee_cents: z.number().int().nullable(),
    provider_fee_details: z.unknown().nullable().optional(),
    commission_cents: z.number().int().nullable(),
    net_amount_cents: z.number().int().nullable(),
    expert_level_id: z.number().int().nullable(),
    quote_id: z.number().int().nullable(),
    project_id: z.number().int().nullable(),
    quote_payment_event_id: z.number().int().nullable(),
    payment_provider: z.string().nullable(),
    provider_payment_intent_id: z.string().nullable(),
    provider_payment_link_id: z.string().nullable(),
    public_notes: z.string().nullable(),
    internal_notes: z.string().nullable(),
    internal_notes_admin: CompactUserSchema.nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
    remaining_hold_cents: z.number().int().nullable(),
    released_hold_cents: z.number().int().optional(),
    debited_hold_cents: z.number().int().optional(),
    refunded_hold_cents: z.number().int().optional(),
    release_transaction_ids: z.array(z.number().int()).optional(),
    expert: CompactUserSchema.nullable().optional(),
    client: CompactUserSchema.nullable().optional(),
    deleted_at: z.string().nullable().optional(),
    deleted_by_admin_id: z.number().int().nullable().optional(),
    delete_note: z.string().nullable().optional(),
    deleted_by_admin: CompactUserSchema.nullable().optional(),
    available_allocations: z.array(AdminWalletAvailableAllocationSchema).optional(),
    unallocated_available_cents: z.number().int().optional(),
    allocated_available_cents: z.number().int().optional(),
    remaining_available_cents: z.number().int().optional(),
    funded_allocations: z.array(AdminWalletFundedAllocationSchema).optional(),
}).passthrough();

export const AdminDeletedWalletTransactionSchema = z.object({
    id: z.number().int(),
    deleted_at: z.string().nullable(),
    deleted_by_admin_id: z.number().int().nullable(),
    delete_note: z.string().nullable(),
    deleted_by_admin: CompactUserSchema.nullable().optional(),
}).passthrough();

const AdminCreateWalletTransactionCommonSchema = z.object({
    hold_source: AdminWalletHoldSourceSchema.optional(),
    amount_cents: z.number().int().min(1),
    currency: z.string().optional(),
    idempotency_key: z.string().optional(),
    public_notes: z.string().nullable().optional(),
    internal_notes: z.string().nullable().optional(),
});

const AdminCreateExternalPaymentTransactionSchema = z.object({
    transaction_kind: z.literal("admin_external_payment"),
    gross_amount_cents: z.number().int().min(1),
    net_amount_cents: z.number().int().min(0),
    project_settled: z.boolean(),
    expert_already_paid: z.boolean(),
    currency: z.literal("USD").optional(),
    idempotency_key: z.string().min(1).max(255),
    quote_id: z.number().int().positive().nullable().optional(),
    public_notes: z.string().nullable().optional(),
    internal_notes: z.string().nullable().optional(),
});

const AdminWalletNonHoldDebitTransactionKindSchema = z.union([
    z.literal("admin_credit"),
    z.literal("admin_debit"),
    z.literal("admin_hold"),
    z.literal("admin_gross_sales_credit"),
    z.literal("admin_gross_sales_debit"),
    z.literal("admin_net_credit"),
    z.literal("admin_net_debit"),
]);

export const AdminCreateWalletTransactionRequestSchema = z.discriminatedUnion(
    "transaction_kind",
    [
        AdminCreateWalletTransactionCommonSchema.extend({
            transaction_kind: z.literal("admin_hold_debit"),
            hold_transaction_ids: z.array(z.number().int().positive()).min(1),
        }),
        AdminCreateWalletTransactionCommonSchema.extend({
            transaction_kind: AdminWalletNonHoldDebitTransactionKindSchema,
            hold_transaction_ids: z.array(z.number().int().positive()).min(1).optional(),
        }),
        AdminCreateExternalPaymentTransactionSchema,
    ],
).superRefine((value, ctx) => {
    if (
        value.transaction_kind === "admin_external_payment" &&
        value.net_amount_cents > value.gross_amount_cents
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["net_amount_cents"],
            message: "Net amount must not exceed gross amount.",
        });
    }
});

export const AdminCreateWalletTransactionResponseSchema = z.union([
    AdminWalletTransactionSchema,
    z.array(AdminWalletTransactionSchema),
]);

export const AdminReleaseWalletHoldRequestSchema = z.object({
    amount_cents: z.number().int().min(1).optional(),
    idempotency_key: z.string().optional(),
    public_notes: z.string().nullable().optional(),
    internal_notes: z.string().nullable().optional(),
});

export const AdminDeleteWalletTransactionRequestSchema = z.object({
    delete_note: z.string().nullable().optional(),
});

export const AdminReconcileWalletAllocationsSummarySchema = z.object({
    wallet_id: z.number().int(),
    allocated_cents: z.number().int(),
    allocations_purged: z.number().int(),
    shortfall_cents: z.number().int(),
    shortfall_transaction_ids: z.array(z.number().int()),
}).passthrough();

export const AdminReconcileAllWalletAllocationsSummarySchema = z.object({
    wallets_reconciled: z.number().int(),
    allocated_cents: z.number().int(),
    allocations_purged: z.number().int(),
    shortfall_cents: z.number().int(),
    wallets: z.array(AdminReconcileWalletAllocationsSummarySchema),
}).passthrough();

export const ExpertQuoteFeeEstimateRequestSchema = z.object({
    acting_org_id: z.number().int().min(1),
    amount_cents: z.number().int().min(1),
    currency: z.string().min(1),
});

export const AdminWalletBalanceBucketSchema = z.object({
    total_owed_cents: z.number().int(),
    available_balance_cents: z.number().int(),
    hold_balance_cents: z.number().int(),
    wallet_count: z.number().int(),
});

export const AdminWalletStatisticsTotalsSchema = AdminWalletBalanceBucketSchema.extend({
    lifetime_gross_cents: z.number().int(),
    lifetime_net_cents: z.number().int(),
    experts_owed_count: z.number().int(),
    experts_with_available_count: z.number().int(),
    experts_with_hold_count: z.number().int(),
    negative_balance_count: z.number().int(),
    negative_balance_cents: z.number().int(),
});

export const AdminWalletCurrencyBucketSchema = AdminWalletBalanceBucketSchema.extend({
    currency: z.string(),
});

export const AdminWalletActivityWindowSchema = z.object({
    earned_cents: z.number().int(),
    admin_adjustments_cents: z.number().int(),
    paid_out_cents: z.number().int(),
    refunded_cents: z.number().int(),
    released_to_available_cents: z.number().int(),
    owed_delta_cents: z.number().int(),
});

export const AdminExpertWalletBalanceSchema = z.object({
    wallet_id: z.number().int(),
    organization_id: z.number().int(),
    organization_type: z.string().nullable(),
    organization_name: z.string().nullable(),
    currency: z.string(),
    available_balance_cents: z.number().int(),
    hold_balance_cents: z.number().int(),
    total_owed_cents: z.number().int(),
    lifetime_gross_cents: z.number().int(),
    lifetime_net_cents: z.number().int(),
    last_transaction_at: z.string().nullable(),
    expert: z.object({
        id: z.number().int(),
        name: z.string().nullable(),
        avatar: z.string().nullable(),
        email: z.string().nullable(),
    }).nullable(),
});

export const AdminWalletStatisticsOverviewSchema = z.object({
    currency: z.string(),
    totals: AdminWalletStatisticsTotalsSchema,
    other_organizations: AdminWalletBalanceBucketSchema,
    by_currency: z.array(AdminWalletCurrencyBucketSchema),
    activity: z.object({
        all_time: AdminWalletActivityWindowSchema,
        last_30_days: AdminWalletActivityWindowSchema,
        last_month: AdminWalletActivityWindowSchema,
        this_month: AdminWalletActivityWindowSchema,
    }),
    top_experts: z.array(AdminExpertWalletBalanceSchema),
});

export const AdminExpertWalletBalancesMetaSchema = z.object({
    total: z.number().int(),
    total_owed_cents: z.number().int(),
    available_balance_cents: z.number().int(),
    hold_balance_cents: z.number().int(),
});

export interface ExpertWalletActingOrgQueryParams extends QueryParams {
    acting_org_id: number;
}

export interface ExpertWalletTransactionsQueryParams extends ExpertWalletActingOrgQueryParams {
    per_page?: number;
    cursor?: string;
}

export interface ExpertPayoutRequestsQueryParams extends ExpertWalletActingOrgQueryParams {
    per_page?: number;
    cursor?: string;
}

export interface AdminPayoutRequestsQueryParams extends QueryParams {
    organization_id?: number;
    status?: PayoutRequestStatus;
    per_page?: number;
    cursor?: string;
}

export interface AdminWalletTransactionsQueryParams extends QueryParams {
    organization_id?: number;
    transaction_kind?: WalletTransactionKind;
    direction?: WalletTransactionDirection;
    currency?: string;
    quote_id?: number;
    project_id?: number;
    quote_payment_event_id?: number;
    related_transaction_id?: number;
    expert_level_id?: number;
    payment_provider?: string;
    provider_payment_intent_id?: string;
    provider_payment_link_id?: string;
    idempotency_key?: string;
    created_from?: string;
    created_to?: string;
    amount_min_cents?: number;
    amount_max_cents?: number;
    eligible_for_hold_release?: boolean;
    search?: string;
    per_page?: number;
    cursor?: string;
}

export type AdminExpertWalletBalancesSort =
    | "total_owed"
    | "available"
    | "hold"
    | "lifetime_gross"
    | "lifetime_net";

export interface AdminWalletStatisticsQueryParams extends QueryParams {
    currency?: string;
    top_limit?: number;
}

export interface AdminExpertWalletBalancesQueryParams extends QueryParams {
    currency?: string;
    search?: string;
    sort?: AdminExpertWalletBalancesSort;
    direction?: "asc" | "desc";
    owed_only?: boolean;
    organization_type?: "expert" | "agency";
    per_page?: number;
    cursor?: string;
}

export type WalletTransactionKind = z.infer<typeof WalletTransactionKindSchema>;
export type AdminWalletTransactionKind = z.infer<typeof AdminWalletTransactionKindSchema>;
export type WalletTransactionDirection = z.infer<typeof WalletTransactionDirectionSchema>;
export type AdminWalletHoldSource = z.infer<typeof AdminWalletHoldSourceSchema>;
export type OrganizationWallet = z.infer<typeof OrganizationWalletSchema>;
export type ExpertWalletSummary = z.infer<typeof ExpertWalletSummarySchema>;
export type ExpertWalletLevelProgress = z.infer<typeof ExpertWalletLevelProgressSchema>;
export type ExpertQuoteFeeEstimateLevel = z.infer<typeof ExpertQuoteFeeEstimateLevelSchema>;
export type ExpertQuoteFeeEstimate = z.infer<typeof ExpertQuoteFeeEstimateSchema>;
export type ExpertWalletTransaction = z.infer<typeof ExpertWalletTransactionSchema>;
export type AdminWalletResponse = z.infer<typeof AdminWalletResponseSchema>;
export type AdminWalletTransaction = z.infer<typeof AdminWalletTransactionSchema>;
export type AdminWalletAvailableAllocation = z.infer<typeof AdminWalletAvailableAllocationSchema>;
export type AdminWalletFundedAllocation = z.infer<typeof AdminWalletFundedAllocationSchema>;
export type AdminReconcileAllWalletAllocationsSummary = z.infer<typeof AdminReconcileAllWalletAllocationsSummarySchema>;
export type AdminReconcileWalletAllocationsSummary = z.infer<typeof AdminReconcileWalletAllocationsSummarySchema>;
export type AdminDeletedWalletTransaction = z.infer<typeof AdminDeletedWalletTransactionSchema>;
export type AdminCreateWalletTransactionRequest = z.infer<typeof AdminCreateWalletTransactionRequestSchema>;
export type AdminCreateWalletTransactionResponse = z.infer<typeof AdminCreateWalletTransactionResponseSchema>;
export type AdminReleaseWalletHoldRequest = z.infer<typeof AdminReleaseWalletHoldRequestSchema>;
export type AdminDeleteWalletTransactionRequest = z.infer<typeof AdminDeleteWalletTransactionRequestSchema>;
export type ExpertQuoteFeeEstimateRequest = z.infer<typeof ExpertQuoteFeeEstimateRequestSchema>;
export type PayoutRequestStatus = z.infer<typeof PayoutRequestStatusSchema>;
export type ExpertPayoutRequest = z.infer<typeof ExpertPayoutRequestSchema>;
export type AdminPayoutRequest = z.infer<typeof AdminPayoutRequestSchema>;
export type AdminPayoutApprovalPreview = z.infer<typeof AdminPayoutApprovalPreviewSchema>;
export type ExpertCreatePayoutRequestRequest = z.infer<typeof ExpertCreatePayoutRequestRequestSchema>;
export type AdminDecidePayoutRequestRequest = z.infer<typeof AdminDecidePayoutRequestRequestSchema>;
export type AdminWalletBalanceBucket = z.infer<typeof AdminWalletBalanceBucketSchema>;
export type AdminWalletStatisticsTotals = z.infer<typeof AdminWalletStatisticsTotalsSchema>;
export type AdminWalletCurrencyBucket = z.infer<typeof AdminWalletCurrencyBucketSchema>;
export type AdminWalletActivityWindow = z.infer<typeof AdminWalletActivityWindowSchema>;
export type AdminExpertWalletBalance = z.infer<typeof AdminExpertWalletBalanceSchema>;
export type AdminWalletStatisticsOverview = z.infer<typeof AdminWalletStatisticsOverviewSchema>;
export type AdminExpertWalletBalancesMeta = z.infer<typeof AdminExpertWalletBalancesMetaSchema>;
